import axios, {
  AxiosInstance,
  AxiosInterceptorManager,
  AxiosPromise,
  AxiosRequestConfig,
} from "axios";
import AuthProvider from "../authentication/AuthProvider";
import { rewriteAxiosRequestConfig } from "./rewriteAxiosRequestConfig";
import axiosRetry from "axios-retry";

export const authorizedApi = axios.create();
authorizedApi.interceptors.request.use(async (config: AxiosRequestConfig) => {
  const token = await AuthProvider.getAuthToken();

  return rewriteAxiosRequestConfig({
    config: config,
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    accessToken: `Bearer ${token}`,
  });
});

export const anonymousApi = axios.create();
anonymousApi.interceptors.request.use((config: AxiosRequestConfig) => {
  return rewriteAxiosRequestConfig({
    config: config,
    baseUrl: process.env.REACT_APP_API_BASE_URL,
  });
});

let cookieAuthorizedApiAddInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use((config: AxiosRequestConfig) => {
    return rewriteAxiosRequestConfig({
      config: { withCredentials: true, ...config },
      baseUrl: process.env.REACT_APP_API_BASE_URL,
    });
  });
};

export const cookieAuthorizedApi = axios.create();
cookieAuthorizedApiAddInterceptors(cookieAuthorizedApi);

let cookieAuthorizedApiWithAuthRefresh: AxiosInstance | undefined = undefined;
export class CookieAuthorizedApiWithAuthRefresh {
  GetInstance(authRefresh: () => Promise<void>): AxiosInstance {
    if (cookieAuthorizedApiWithAuthRefresh) {
      return cookieAuthorizedApiWithAuthRefresh;
    }

    cookieAuthorizedApiWithAuthRefresh = axios.create();
    cookieAuthorizedApiAddInterceptors(cookieAuthorizedApiWithAuthRefresh);
    axiosRetry(cookieAuthorizedApiWithAuthRefresh, {
      retries: 1,
      retryCondition: async (error) => {
        const shallRetry = error?.response?.status === 401;
        if (shallRetry) {
          await authRefresh();
        }
        return shallRetry;
      },
    });
    return cookieAuthorizedApiWithAuthRefresh;
  }
}

export interface BaseApi {
  interceptors: {
    request: AxiosInterceptorManager<AxiosRequestConfig>;
  };
  post<T = any>(
    url: string,
    data: any,
    config?: AxiosRequestConfig
  ): AxiosPromise<T>;
  get<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): AxiosPromise<T>;
  put<T = any>(
    url: string,
    data: any,
    config?: AxiosRequestConfig
  ): AxiosPromise<T>;
  delete<T = any>(
    url: string,
    params?: any,
    config?: AxiosRequestConfig
  ): AxiosPromise<T>;
}

export class AlwaysPassingBaseApi implements BaseApi {
  private lastCall = {
    url: "",
    data: null,
  };

  interceptors: { request: AxiosInterceptorManager<AxiosRequestConfig> };

  constructor(private response: any) {
    this.interceptors = { request: { use: jest.fn(), eject: jest.fn() } };
  }

  post(url: string = "", data: any = {}) {
    this.lastCall = {
      url,
      data,
    };

    return Promise.resolve(this.response);
  }

  get(url: string = "", params: any = {}) {
    this.lastCall = { url, data: params };
    return Promise.resolve(this.response);
  }

  put(url: string = "", data: any = {}) {
    this.lastCall = {
      url,
      data,
    };

    return Promise.resolve(this.response);
  }

  delete(url: string = "", params: any = {}) {
    this.lastCall = { url, data: params };

    return Promise.resolve(this.response);
  }

  public calledWithUrl() {
    return this.lastCall.url;
  }

  public calledWithData() {
    return this.lastCall.data;
  }
}
